import { TrainModelBodyType } from 'api/models/trainModel';
import { PostModelPredictionRequestBody } from 'api/models/predict';
import { isValidJSON } from 'utils/isValidJSON';
import _set from 'lodash/set';
import { FieldValueMapType, FormikInitialValues, ModelFormModeEnum } from './types';

// constants
export const TrainableHrefs: Record<string, string> = {
  'text-to-text': 'https://docs.clarifai.com/portal-guide/model/model-types/text-to-text/',
  'visual-classifier': 'https://docs.clarifai.com/api-guide/model/model-types/#visual-classifier',
  'visual-detector': 'https://docs.clarifai.com/api-guide/model/model-types/#visual-detector',
  'embedding-classifier': 'https://docs.clarifai.com/api-guide/model/model-types/#embedding-classifier',
  clusterer: 'https://docs.clarifai.com/api-guide/model/model-types/#clusterer',
  DEFAULT: 'https://docs.clarifai.com/api-guide/model/model-types/#trainable-models',
};
export const DATASET_ID_PATH = 'train_info.dataset';
export const DATASET_VERSION_ID_PATH = 'train_info.dataset.version';
export const BASE_EMBED_MODEL = 'input_info.base_embed_model';
export const TRAIN_TEMPLATE_PATH = 'train_info.params.template';
const EXCLUDED_FIELDS = new Set(['output_info.output_config.closed_environment']);

const ADVANCED_MODEL_FIELDS = new Set([
  'output_info.params.select_concepts',
  'output_info.params.detection_threshold',
  'output_info.params.max_concepts',
  'output_info.params.min_value',
]);

export const BASIC_MODEL_FIELDS = new Set([DATASET_ID_PATH, DATASET_VERSION_ID_PATH]);

// helpers
export const getDocsHref = (modelType: string, trainable: boolean, creatable: boolean): string => {
  if (trainable) {
    return TrainableHrefs[modelType] || TrainableHrefs.DEFAULT;
  }
  if (creatable) {
    return 'https://docs.clarifai.com/api-guide/model/model-types/#non-trainable-models-fixed-function-operators';
  }
  // fallback just in case?
  return 'https://docs.clarifai.com/api-guide/model/model-types';
};

export const selectModelTypeFieldTypeInfo = (path: string, values: FormikInitialValues, fieldEntity?: CF.API.Models.ModelTypeFieldsEntity) => {
  const { basicFields, advancedFields, trainingTemplateFields, fieldValueMap } = values;

  if (path === 'modelId') {
    // Model ID Element
    return {
      description: 'A memorable, human-readable model-id with a maximum of 48-characters',
      placeholder: 'Enter a model ID...',
      default_value: '',
      required: true,
    };
  }

  if (path.includes('output_info.params') && fieldEntity) {
    return {
      description: fieldEntity?.description,
      default_value: fieldEntity?.default_value,
    };
  }

  let allFields = (basicFields || []).concat(advancedFields || []);
  const selectedTemplate = fieldValueMap[TRAIN_TEMPLATE_PATH];
  const modelTypeFields = trainingTemplateFields.find((field) => field.id === selectedTemplate)?.model_type_fields || [];
  allFields = allFields.concat(modelTypeFields);
  const fieldInfo = allFields.find((field) => field.path === path);
  if (fieldInfo === undefined) return {};

  if (path === 'train_info.dataset') {
    return {
      description: (
        <div>
          <p>
            Dataset to use for training this model. If there&apos;s no dataset, all compatible labeled inputs in this app are used as a default
            dataset. You can also upload labeled datasets with Python SDK{' '}
            <a href="https://github.com/Clarifai/examples/tree/main/datasets/upload">Learn More</a>
          </p>
        </div>
      ),
      placeholder: fieldInfo.placeholder,
      required: fieldInfo.required || false,
      model_type_range_info: fieldInfo.model_type_range_info,
      model_type_enum_options: fieldInfo.model_type_enum_options,
    };
  }

  return {
    description: fieldInfo.description,
    placeholder: fieldInfo.placeholder,
    required: fieldInfo.required || false,
    model_type_range_info: fieldInfo.model_type_range_info,
    model_type_enum_options: fieldInfo.model_type_enum_options,
  };
};

export const splitFieldsByUIPriority = (
  fields: CF.API.Models.ModelTypeFieldsEntity[],
): readonly [CF.API.Models.ModelTypeFieldsEntity[], CF.API.Models.ModelTypeFieldsEntity[], CF.API.Models.ModelTypeEnumOptionsEntity[]] => {
  let basic = [] as CF.API.Models.ModelTypeFieldsEntity[];
  let advanced = [] as CF.API.Models.ModelTypeFieldsEntity[];
  let trainingTemplateFields = [] as CF.API.Models.ModelTypeEnumOptionsEntity[];

  fields.forEach((field) => {
    if (field.model_type_enum_options) {
      trainingTemplateFields = field.model_type_enum_options as CF.API.Models.ModelTypeEnumOptionsEntity[];
    }

    if (EXCLUDED_FIELDS.has(field.path)) {
      return;
    }
    // if the field's path exists in one of the two lists, use that.
    if (ADVANCED_MODEL_FIELDS.has(field.path)) {
      advanced.push(field);
      return;
    }
    if (BASIC_MODEL_FIELDS.has(field.path)) {
      basic.push(field);
      return;
    }

    // if the field's path is NOT in either list, use the internal_only flag to separate it.
    if (field.internal_only) {
      advanced.push(field);
      return;
    }
    basic.push(field);
  });

  basic = mapFieldsWithTitles(basic).sort(sortFieldsByMap(BASIC_FIELD_ORDER_MAP)).map(fieldEnhancer);
  advanced = mapFieldsWithTitles(advanced).sort(sortFieldsByMap(ADVANCED_FIELD_ORDER_MAP)).map(fieldEnhancer);

  return [basic, advanced, trainingTemplateFields];
};

export const getFormInitialValues = (
  modelTypeId: string,
  modelTypes: CF.API.Models.ModelTypesEntity[],
  appDatasets: CF.API.Datasets.Dataset[],
  mode: ModelFormModeEnum,
  model?: CF.API.Models.Model,
): FormikInitialValues => {
  const getInputValue = (obj: CF.API.Models.ModelVersion, path: string, defaultValue: unknown): unknown => {
    const objPath = path.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result = objPath.reduce((acc, key) => acc && acc[key], obj as any);
    if (typeof result === 'object' && [DATASET_ID_PATH, DATASET_VERSION_ID_PATH, BASE_EMBED_MODEL].includes(path)) {
      result = result.id;
    }
    return result === undefined ? (defaultValue === undefined ? null : defaultValue) : result;
  };

  const modelId = model?.id || '';
  const modelType = (modelTypeId as string) || model?.model_type_id || '';
  const modelTypeDetails = modelTypes.find((modelTypeObject) => modelTypeObject.id === modelType);
  const modelTitle = modelTypeDetails?.title || '';
  const modelTypeDescription = modelTypeDetails?.description || '';
  const isTrainable = modelTypeDetails?.trainable || false;
  const isCreatable = modelTypeDetails?.creatable || false;

  // eval_info should only be used for evaluating model. Not for creating or editing.
  const modelTypeFields = modelTypeDetails?.model_type_fields?.filter((field) => !field.path.includes('eval_info')) || [];
  const [basicFields, advancedFields, trainingTemplateFields] = splitFieldsByUIPriority(modelTypeFields);
  // Initialize fieldValueMap with either values from model if it exists or the default values
  const fieldValueMap: FieldValueMapType = modelTypeFields.reduce(
    (acc, field) => {
      const fieldValue = model ? getInputValue(model.model_version, field.path, field.default_value) : field.default_value;
      if (field.path === TRAIN_TEMPLATE_PATH) {
        // Initialize training template settings with version config values
        const modelTypeEnumOption = trainingTemplateFields.find((options) => options.id === (fieldValue as string))?.model_type_fields || [];
        modelTypeEnumOption.forEach((param) => {
          const { path: optionPath, default_value } = param;
          const templateFieldValue = model ? getInputValue(model.model_version, optionPath, default_value) : default_value;
          acc[optionPath] = templateFieldValue;
        });
      }
      acc[field.path] = fieldValue;
      return acc;
    },
    { modelId } as FieldValueMapType,
  );

  const formInitialValues: FormikInitialValues = {
    mode,
    model,
    modelId,
    modelType,
    modelTitle,
    modelTypeDescription,
    appDatasets,
    isTrainable,
    isCreatable,
    basicFields,
    advancedFields,
    trainingTemplateFields,
    fieldValueMap,
    errorString: '',
    trainingTimeEstimate: '',
    totalInputCount: '',
    isSaving: false,
    isModelIdSubmitted: mode !== ModelFormModeEnum.CREATE,
  };

  return formInitialValues;
};

export const transformValue = (defaultValue: unknown, value: unknown) => {
  if (Array.isArray(defaultValue) && !Array.isArray(value)) {
    return [value];
  } else if (typeof defaultValue === 'string') {
    return String(value);
  } else if (typeof defaultValue === 'number') {
    return Number(value);
  } else if (typeof defaultValue === 'boolean') {
    return Boolean(value);
  }
  return value;
};

export const getModelPredictRequestBody = (reqBody: FieldValueMapType): PostModelPredictionRequestBody => {
  const excudedKeys = ['train_info', 'modelId', 'detection_threshold', 'input_info', 'ignore__'];
  const requestBody = {};
  Object.entries(reqBody).forEach(([path, value]) => {
    if (!excudedKeys.some((excluded) => path.includes(excluded))) {
      const splitPath = path.split('.');
      if (splitPath.includes('output_info')) {
        _set(requestBody, `model.model_version.${path}`, value);
      } else {
        _set(requestBody, path, value);
      }
    }
  });
  return requestBody as PostModelPredictionRequestBody;
};

export const getTrainModelRequestBody = (reqBody: FieldValueMapType): TrainModelBodyType => {
  const trainInfoObject = { dataset: {} as FieldValueMapType, params: {} as FieldValueMapType };
  const outputInfoDataObject = { params: {} as FieldValueMapType, data: {} as FieldValueMapType, output_config: {} as FieldValueMapType };
  Object.entries(reqBody).forEach(([path, value]) => {
    const splitPath = path.split('.');
    const parsedValue = isValidJSON(value as string, true) ? JSON.parse(value as string) : value;
    if (splitPath.includes('train_info')) {
      const nestedKey = splitPath.pop();
      if (path === DATASET_ID_PATH) {
        trainInfoObject.dataset.id = parsedValue;
        trainInfoObject.params.dataset_id = parsedValue;
      } else if (path === DATASET_VERSION_ID_PATH) {
        trainInfoObject.dataset.version = { id: parsedValue };
        trainInfoObject.params.dataset_version_id = parsedValue;
      } else if (path === 'train_info.resume_from_model') {
        // trainInfoObject.resume_from_model = '';
        // can't include param due to malformed request
        // missing field
      } else if (nestedKey) {
        trainInfoObject.params[nestedKey] = parsedValue;
      }
    } else if (splitPath.includes('output_info')) {
      const nestedKey = splitPath.pop();
      const invalidKeys = ['detection_threshold'];
      if (nestedKey && !invalidKeys.includes(nestedKey)) {
        if (splitPath.includes('params')) {
          outputInfoDataObject.params[nestedKey] = value;
        } else if (splitPath.includes('output_config')) {
          outputInfoDataObject.output_config[nestedKey] = value;
        } else {
          outputInfoDataObject.data[nestedKey] = value;
        }
      }
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { model_versions: [{ train_info: trainInfoObject, output_info: outputInfoDataObject }] } as any;
};

export const getIsSubmitDisabled = (values: FormikInitialValues): boolean => {
  /*
  Returns true if all the required fields are filled out, if model ID
  is not taken, and button is not already clicked (in saving state)
  */
  const getRequiredFieldFilled = (fieldValueMap: FieldValueMapType, path: string): boolean => {
    const value = fieldValueMap[path];
    return Array.isArray(value) ? !value.length : !value;
  };

  const getEmptyFieldList = (fieldValueMap: FieldValueMapType, requiredFields: string[], formMode: ModelFormModeEnum = ModelFormModeEnum.CREATE) => {
    const fieldList = formMode === ModelFormModeEnum.CREATE ? ['modelId', ...requiredFields] : [...requiredFields];
    return fieldList.filter((path) => getRequiredFieldFilled(fieldValueMap, path)).map((field) => (field === 'modelId' ? 'Model ID' : `${field}`));
  };

  // TODO: Consolidate error variables and improve management
  const { mode, basicFields, advancedFields, errorString, errorDetails, errorDescription, isSaving, fieldValueMap } = values;
  const allFields = basicFields.concat(advancedFields);
  const requiredFields = allFields.filter((field) => field.required).map((field) => field.path);
  const emptyRequiredFields = getEmptyFieldList(fieldValueMap, requiredFields, mode);
  const isIdTaken = Boolean(errorString);
  const hasErrors = Boolean(errorDetails || errorDescription || isIdTaken);
  const isSubmitDisabled = emptyRequiredFields.length > 0 || hasErrors || isSaving;
  return isSubmitDisabled;
};

// ---------- Form field reordering logic starts -------------

// TYPES
export type MappedField = CF.API.Models.ModelTypeFieldsEntity & { prependTitle: string; prependSubtitles: string[] };
export type MappedFields = MappedField[];

type FieldOrderMap = { [x: string]: number };

// FIELD ORDERING
// Use this map to reorder form fields to desired index
export const BASIC_FIELD_ORDER_MAP: FieldOrderMap = {
  'train_info.dataset': 1,
  'train_info.dataset.version': 2,
};
export const ADVANCED_FIELD_ORDER_MAP: FieldOrderMap = {
  'output_info.output_config.hyper_params': Number.MAX_SAFE_INTEGER, // internal-only params last
  'output_info.output_config.training_timeout': Number.MAX_SAFE_INTEGER, // internal-only params last
};

const getFieldOrder = (map: { [x: string]: number }, path: string): number => map[path] || Number.MAX_SAFE_INTEGER - 1;
export const sortFieldsByMap =
  (map: FieldOrderMap) =>
  <T extends MappedFields[0]>(f1: T, f2: T) =>
    getFieldOrder(map, f1.path) - getFieldOrder(map, f2.path);

// FIELD MAPPING
export type EnhancedField = {
  config: {
    tooltip?: string;
    help?: string;
  };
};

type FieldEnhancer = { [x: string]: EnhancedField };
const FIELD_ENHANCER: FieldEnhancer = {
  'output_info.params.select_concepts': {
    config: {
      tooltip:
        "Filter out concepts that you added to help model training but don't want to show in your predictions. These are usually explicit negative concepts such as 'not-dog', 'not-person', or 'other' to help improve model performance.",
    },
  },
  'train_info.dataset': {
    config: {
      help: 'A new dataset is automatically generated with all inputs in the app if you don’t have a dataset',
    },
  },
  'train_info.dataset.version': {
    config: {
      help: 'A dataset version is automatically generated from selected dataset if you don’t have a dataset version.',
    },
  },
};
const enhanceField =
  <T extends Record<string, unknown>>(enhancer: Record<string, T>) =>
  <F extends MappedField>(field: F): T & F => ({ ...field, ...enhancer[field.path] });

export const fieldEnhancer = enhanceField(FIELD_ENHANCER);

export const mapFieldsWithTitles = (fields: CF.API.Models.ModelTypeFieldsEntity[]): MappedFields => {
  const fieldsWithTitles: MappedFields = [];
  let stack: string[] = []; // used to track path
  const shallowClonedFields = [...fields];
  // fields must be sorted for this logic to work
  // template must be last field
  shallowClonedFields.sort((a, b) => {
    if (a.path === 'train_info.params.template') {
      return 1;
    }
    if (b.path === 'train_info.params.template') {
      return -1;
    }
    if (a.path < b.path) {
      return -1;
    }
    if (a.path > b.path) {
      return 1;
    }
    return 0;
  });

  for (const field of shallowClonedFields) {
    const path = field.path;
    const pathItems = path.split('.');

    let level = 0;
    let prependTitle = '';
    const prependSubtitles = [];

    for (const pathItem of pathItems) {
      if (stack[level] !== pathItem) {
        if (level === 0) {
          prependTitle = pathItem;
        } else {
          prependSubtitles.push(pathItem);
        }

        // if true, below logic acts as a reset stack array or pop method on the stack depending on level value
        if (stack[level] !== undefined) {
          stack = stack.slice(0, level);
        }

        stack.push(pathItem);
      }

      level += 1;
    }

    fieldsWithTitles.push({ ...field, prependTitle, prependSubtitles });
  }

  return fieldsWithTitles;
};

// ---------- Form field reordering logic ends -------------
